// The search parameters every route accepts (TanStack Router `validateSearch`), in one place so each
// is tested (J-216). A value that fails its check is DROPPED, never thrown: the router renders its
// error page for a thrown search check and logs the raw Zod error, and the owner's matrix run found a
// typed Performance threshold (-5, 1e21, text) doing exactly that on 1,136 cells (inbox file 22,
// item 2; inbox 06 PR D item 8). Each field is checked on its own, so one bad value leaves the rest.
//
// The router parses a typed or pasted `tin=991000003` as a number, and `tab=134` too, so the fields
// that are digits are coerced back to strings before their check.
import { z } from "zod";

/** `schema`, optional, with any value that fails it read as absent. */
function dropInvalid<T extends z.ZodType>(schema: T) {
  return schema.optional().catch(undefined);
}

const digits = (n: number) => z.coerce.string().regex(new RegExp(`^\\d{${n}}$`));

/** J-232: a performance group's id, as `/performance-groups/:groupId` names it (`group=<id>`). */
const groupId = dropInvalid(z.coerce.number().int().positive().max(2_147_483_647));

/**
 * J-277 (QB3 items 8 and 8b): "View by" on Performance and Practice. The group view is the default, so
 * the URL names the practice view (`view=practices`); `view=groups`, which older links carry, still opens
 * the group view (`lib/screen-view.ts`).
 */
const screenViewKey = dropInvalid(z.enum(["groups", "practices"]));

/** The ACO screens' year and ACO (J-059), read by the signed-in layout for every child route. */
export const acoContextSearchSchema = z.object({
  year: dropInvalid(z.coerce.number().int().min(2000).max(2100)),
  aco: dropInvalid(z.string().min(1)),
});

/**
 * Screen 1, Performance, at /groups (J-060, J-202). `sort` is `{measureKey}:{asc|desc}` and `filter`
 * `{measureKey|any}:{below|above}:{value}`, the value a rate from 0 up, written with digits only.
 * J-277 (item 8b): `group` is the practice view's Performance group filter.
 */
export const groupsSearchSchema = z.object({
  view: screenViewKey,
  group: groupId,
  sort: dropInvalid(z.string().regex(/^\d+:(asc|desc)$/)),
  filter: dropInvalid(z.string().regex(/^(\d+|any):(below|above):\d+(\.\d+)?$/)),
});

/** A performance group's measures (J-202): the measure whose patients are listed, by `measureKey`. */
export const groupSearchSchema = z.object({
  measure: dropInvalid(z.coerce.number().int().positive()),
  /**
   * J-268 (QB3 item 2): for a group whose practices span ACOs, the ACO filter's choice by ACO code;
   * omitted is "Both", the group's figures over all its practices.
   */
  groupAco: dropInvalid(z.string().min(1).max(64)),
});

/**
 * Screen 11, the practice dashboard (J-086): the practice's TIN and the clinician's NPI. J-232: the group
 * view, `group` naming the group, for holders of ACO-wide scope only. J-277 (items 8, 8b, 8c): the group
 * view is the default and `view=practices` names the practice view; `group` is also the practice view's
 * Performance group filter, and `tin` the group view's Practice field.
 */
export const practiceSearchSchema = z.object({
  tin: dropInvalid(digits(9)),
  npi: dropInvalid(digits(10)),
  view: screenViewKey,
  group: groupId,
  /** J-268: the group view's ACO filter for a group whose practices span ACOs; omitted is "Both". */
  groupAco: dropInvalid(z.string().min(1).max(64)),
});

/** Screen 12, the quality report (J-087, J-201, J-209). */
export const reportSearchSchema = z.object({
  tin: dropInvalid(digits(9)),
  npi: dropInvalid(digits(10)),
  tab: dropInvalid(z.coerce.string()),
  status: dropInvalid(z.string()),
  /** J-209: the "Action to take" filter of a measure tab. */
  action: dropInvalid(z.string()),
  /** J-201: "Show: All patients | Patients with open gaps". */
  show: dropInvalid(z.enum(["all", "open"])),
  /** J-232: the Performance group filter, for holders of ACO-wide scope only. */
  group: groupId,
  /** J-275: the Facility filter: a facility's name, or `FACILITY_NOT_KNOWN`; not a patient value. */
  facility: dropInvalid(z.string().min(1).max(300)),
});

/** J-232: the search keys only a user with ACO-wide scope may carry (the group view and filter). */
const GROUP_SCOPED_KEYS = ["view", "group"] as const;

/**
 * J-232 (the owner's inbox file 33, item 4): the search a user may keep. With ACO-wide scope it is
 * `search` itself; without it, `search` with `view` and `group` dropped, so no read is sent with a group
 * the server would refuse with 403. Returns undefined when nothing was dropped.
 */
export function dropGroupScope<T extends object>(search: T, acoWide: boolean): T | undefined {
  if (acoWide) return undefined;
  const held = search as Record<string, unknown>;
  if (GROUP_SCOPED_KEYS.every((key) => held[key] === undefined)) return undefined;
  const next: Record<string, unknown> = { ...held };
  for (const key of GROUP_SCOPED_KEYS) delete next[key];
  return next as T;
}

/** Screen 10's audit log (J-073, J-185): one resource, by an opaque id such as `bundle:<uuid>`. */
export const auditSearchSchema = z.object({
  resourceType: dropInvalid(z.string().min(1).max(64)),
  resourceId: dropInvalid(z.string().min(1).max(200)),
});

/** The sign-in page: where to return, why the user is here (J-209, J-210), and the idle minutes. */
export const loginSearchSchema = z.object({
  redirect: dropInvalid(z.string()),
  /** `locked`: Lock session ended the session and sign-in returns to the page (J-209). */
  /** `password_reset`: the reset page sends the user here after a new password was set (J-246). */
  reason: dropInvalid(z.enum(["idle", "expired", "locked", "password_reset"])),
  // J-210: the idle sign-out passes the server's timeout, so the message names the configured minutes.
  minutes: dropInvalid(z.coerce.number().int().min(1).max(15)),
});

/**
 * The reset page (J-246): the single-use token from the "Forgot password?" mail. Better Auth's tokens
 * are 24 letters and digits; anything else is dropped, and the page says the link is incomplete.
 */
export const resetPasswordSearchSchema = z.object({
  token: dropInvalid(z.coerce.string().regex(/^[A-Za-z0-9]{16,128}$/)),
});

/** The second sign-in step: where to return after it. */
export const twoFactorSearchSchema = z.object({
  redirect: dropInvalid(z.string()),
});
